import React from 'react'
import { Box, Paper, Typography, useTheme, alpha } from '@mui/material'
import { TrackerState } from '../../types/gotypes'
import { ProgressRing, Reveal } from './atoms'

const Card: React.FC<{ children: React.ReactNode; index: number; accent: string }> = ({ children, index, accent }) => {
  const theme = useTheme()
  return (
    <Reveal index={index}>
      <Paper
        elevation={0}
        sx={{
          flex: '1 1 240px',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s ease, border-color 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(15,23,42,0.06)',
            borderColor: alpha(accent, 0.3),
          },
        }}
      >
        {children}
      </Paper>
    </Reveal>
  )
}

const Label: React.FC<{ title: string; detail: string }> = ({ title, detail }) => {
  const theme = useTheme()
  return (
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: theme.palette.text.primary }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: theme.palette.text.secondary, mt: 0.25 }}>{detail}</Typography>
    </Box>
  )
}

// SummaryBar shows one KPI ring per section ("done of total"), plus the
// overall programme percentage. The section list is generic — the tracker
// decides how many sections there are (Adam, 23 Sep).
export const SummaryBar: React.FC<{ tracker: TrackerState }> = ({ tracker }) => {
  const theme = useTheme()
  const { summary, branding } = tracker
  // One calm accent across the KPIs (Sky blue) for a cleaner, unified look.
  const primary = branding.primaryColor || theme.palette.primary.main

  return (
    <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', px: { xs: 2, md: 4 }, py: 2 }}>
      {summary.sections.map((section, i) => {
        const pct = section.total ? (section.done / section.total) * 100 : 0
        return (
          <Card key={i} index={i} accent={primary}>
            <ProgressRing pct={pct} value={section.done} total={section.total} color={primary} />
            <Label title={section.name} detail={section.doneStatus || 'Done'} />
          </Card>
        )
      })}
      <Card index={summary.sections.length} accent={primary}>
        <ProgressRing pct={summary.overallPct} value={summary.overallPct} unit="%" color={primary} />
        <Label title="Overall" detail="Programme progress" />
      </Card>
    </Box>
  )
}

export default SummaryBar
